import { Typography } from "@/frontend/shared/ui/Typography";
import styles from "./AddCompaniesFixedModal.module.scss";
import { CaretDownIcon, CaretLeftIcon, CheckIcon, WarningCircle1Icon, XIcon } from "@/frontend/shared/assets/icons";
import { useEffect, useState } from "react";
import clsx from "clsx";
import { ESections, TCompanyInfo } from "../../model/types/types";
import { EDataStatus } from "@/frontend/features/Company/model/types/types";
import { FileType } from "@/frontend/shared/ui/FileType/FileType";
import { getFileExtension } from "@/frontend/shared/lib/getFileExtension/getFileExtension";

type Props = {
  portfolioCompaniesBulkData: TCompanyInfo[];
  fileUploadProgresses: Record<string, number>;
};

type TFileStatus = "pending" | "error" | "success";

type TFileFinalInfo = {
  name: string;
  uploadMessage: string;
  progress: number;
  status: TFileStatus;
};

export function AddCompaniesFixedModal({ portfolioCompaniesBulkData, fileUploadProgresses }: Props) {
  const [isModalOpen, setIsModalOpen] = useState(true);
  const [isExpanded, setIsExpanded] = useState(true);
  const [modalSection, setModalSection] = useState<ESections>(ESections.ADD_SECTION);
  const [finalFiles, setFinalFiles] = useState<TFileFinalInfo[]>([]);

  const companiesWithError = portfolioCompaniesBulkData.filter((item) => item.parsingStatus === EDataStatus.error);

  useEffect(() => {
    const fileUploadEntries = Object.entries(fileUploadProgresses);

    const formattedFileUploadProgresses: TFileFinalInfo[] = fileUploadEntries.map(([name, progress]) => ({
      name,
      progress: Math.round(progress / 2),
      uploadMessage: "Uploading files",
      status: "pending"
    }));

    const formattedBulkData: TFileFinalInfo[] = portfolioCompaniesBulkData.map((company) => {
      let message = "";
      let progress = 50;
      let status: TFileStatus = "pending";

      const loadingProgress = Math.round(company.parsingProgress * 50); // * 100 / 2
      switch (company.parsingStatus) {
        case EDataStatus.parsing:
          message = "Processing document";
          progress = 51;
          status = "pending";
          break;
        case EDataStatus.processing:
          message = company.parsingMessage || "Creating company profile";
          progress = 50 + loadingProgress;
          status = "pending";
          break;
        case EDataStatus.error:
          message = company.parsingMessage || "Error parsing the pitch deck. Please upload a new file or try again.";
          progress = 100;
          status = "error";
          break;
        default:
          message = "Company created";
          progress = 100;
          status = "success";
          break;
      }

      return {
        name: company.name,
        progress,
        uploadMessage: message,
        status
      };
    });

    const currentFiles: TFileFinalInfo[] = [...formattedFileUploadProgresses, ...formattedBulkData];

    const newFiles: TFileFinalInfo[] = [];
    for (const file of finalFiles) {
      const updatedFile = currentFiles.find((currentFile) => currentFile.name === file.name);
      if (!updatedFile) {
        newFiles.push({
          name: file.name,
          progress: 50,
          status: "pending",
          uploadMessage: "Processing document"
        });
      } else {
        newFiles.push(updatedFile);
      }
    }

    for (const file of currentFiles) {
      if (!finalFiles.find((finalFile) => finalFile.name === file.name)) {
        newFiles.push(file);
      }
    }
    setFinalFiles(newFiles);
  }, [portfolioCompaniesBulkData, fileUploadProgresses, JSON.stringify(fileUploadProgresses)]);

  switch (true) {
    case !isModalOpen:
      return null;

    case modalSection === ESections.ADD_SECTION && finalFiles.length > 0:
      return (
        <div className={clsx(styles.container, isExpanded && finalFiles.length > 0 && styles.containerExpanded)}>
          <div className={styles.header}>
            <Typography variant="heading-7" color="greyscale800">
              Add Companies
            </Typography>
            <div className={styles.headerRightSide}>
              {companiesWithError.length > 0 && (
                <div
                  className={styles.errorIndicatorContainer}
                  onClick={() => setModalSection(ESections.ERRORS_SECTION)}
                >
                  <WarningCircle1Icon />
                  <Typography variant="label-5" color="error-red">
                    {companiesWithError.length}
                  </Typography>
                </div>
              )}
              <CaretDownIcon
                className={clsx(styles.headerCaretDownIcon, isExpanded && styles.headerCaretDownIconExpanded)}
                onClick={() => setIsExpanded((prev) => !prev)}
              />
              <XIcon className={styles.headerXIcon} onClick={() => setIsModalOpen(false)} />
            </div>
          </div>

          <div className={styles.content}>
            {finalFiles.map((finalFile) => {
              const { status, uploadMessage, progress, name } = finalFile;
              const fileIcon = (
                <FileType type={getFileExtension(name).toLocaleUpperCase()} isError={status == "error"} />
              );

              switch (status) {
                case "error":
                  return (
                    <div className={styles.row} key={name}>
                      <div className={styles.rowContent}>
                        {fileIcon}
                        <div className={styles.companyInfo}>
                          <div className={styles.companyShortInfo}>
                            <Typography variant="label-4" color="error-red">
                              {uploadMessage || "File Upload Failed"}
                            </Typography>
                            <Typography variant="body-3" color="error-red">
                              {name}
                            </Typography>
                          </div>
                        </div>
                      </div>
                      <div className={styles.progressContainer}>
                        <div className={clsx(styles.progress, styles.errorProgress)} />
                      </div>
                    </div>
                  );
                case "success":
                  return (
                    <div className={styles.row} key={name}>
                      <div className={styles.rowContent}>
                        {fileIcon}
                        <div className={styles.companyInfo}>
                          <div className={styles.companyShortInfo}>
                            <Typography variant="label-4" color="greyscale800">
                              {name}
                            </Typography>
                            <Typography variant="body-3" color="greyscale600">
                              {uploadMessage}
                            </Typography>
                          </div>
                          <div className={styles.checkIconContainer}>
                            <CheckIcon className={styles.checkIcon} />
                          </div>
                        </div>
                      </div>
                      <div className={styles.progressContainer}>
                        <div className={clsx(styles.progress, styles.successProgress)} />
                      </div>
                    </div>
                  );

                default:
                  return (
                    <div className={styles.row} key={name}>
                      <div className={styles.rowContent}>
                        {fileIcon}
                        <div className={styles.companyInfo}>
                          <div className={styles.companyShortInfo}>
                            <Typography variant="label-4" color="greyscale800">
                              {progress}%
                            </Typography>
                            <Typography variant="body-3" color="greyscale600">
                              {name}
                            </Typography>
                          </div>
                        </div>
                      </div>

                      <div className={styles.progressContainer}>
                        <div className={styles.progress} style={{ width: `${progress}%` }} />
                      </div>
                    </div>
                  );
              }
            })}
          </div>
        </div>
      );

    case modalSection === ESections.ERRORS_SECTION && isModalOpen && portfolioCompaniesBulkData.length > 0:
      return (
        <div
          className={clsx(
            styles.container,
            isExpanded && portfolioCompaniesBulkData.length > 0 && styles.containerExpanded,
            (!isModalOpen || portfolioCompaniesBulkData.length < 0) && styles.containerHidden
          )}
        >
          <div className={styles.header}>
            <div className={styles.headerLeftSide}>
              <CaretLeftIcon
                className={styles.headerCaretLeftIcon}
                onClick={() => setModalSection(ESections.ADD_SECTION)}
              />
              <Typography variant="heading-7" color="greyscale800">
                Failed Documents
              </Typography>
            </div>
            <div className={styles.headerRightSide}>
              {companiesWithError.length > 0 && (
                <div
                  className={styles.errorIndicatorContainer}
                  onClick={() => setModalSection(ESections.ERRORS_SECTION)}
                >
                  <WarningCircle1Icon />
                  <Typography variant="label-5" color="error-red">
                    {companiesWithError.length}
                  </Typography>
                </div>
              )}
              <CaretDownIcon
                className={clsx(
                  styles.headerCaretDownIcon,
                  isExpanded && portfolioCompaniesBulkData.length > 0 && styles.headerCaretDownIconExpanded
                )}
                onClick={() => setIsExpanded((prev) => !prev)}
              />
              <XIcon className={styles.headerXIcon} onClick={() => setIsModalOpen(false)} />
            </div>
          </div>

          <div className={styles.content}>
            {companiesWithError.map((item, index) => {
              const { name, parsingMessage } = item;

              return (
                <div className={styles.row} key={name}>
                  <div className={styles.rowContent}>
                    <FileType type={getFileExtension(name).toLocaleUpperCase()} isError={true} />
                    <div className={styles.companyInfo}>
                      <div className={styles.companyShortInfo}>
                        <Typography variant="label-4" color="error-red">
                          {parsingMessage || "File Upload Failed"}
                        </Typography>
                        <Typography variant="body-3" color="error-red">
                          {name}
                        </Typography>
                      </div>
                    </div>
                  </div>
                  <div className={styles.progressContainer}>
                    <div className={clsx(styles.progress, styles.errorProgress)} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      );
  }
}
